import './field.js';

import { ShadowlessElement, WithDisposable } from '@blocksuite/block-std';
import { SignalWatcher, computed } from '@lit-labs/preact-signals';
import { css, nothing, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { keyed } from 'lit/directives/keyed.js';
import { repeat } from 'lit/directives/repeat.js';
import { html } from 'lit/static-html.js';

import { popFilterableSimpleMenu } from '../../../../_common/components/index.js';
import type { CellRenderProps } from '../../column/index.js';
import { renderUniLit } from '../../utils/uni-component/uni-component.js';
import type { Column } from '../../view-manager/column.js';
import type { SingleView } from '../../view-manager/single-view.js';
import { dataViewCommonStyle } from '../css-variable.js';
import type { DetailSlotProps, DetailSlots } from '../data-source/base.js';
import { PlusIcon } from '../icons/index.js';
import { DetailSelection } from './selection.js';

const arrowDown = html`<svg
  width="24"
  height="24"
  viewBox="0 0 24 24"
  fill="currentColor"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M17.5073 13.0032C17.2022 12.723 16.7278 12.7432 16.4476 13.0483L12.75 17.0745L12.75 5C12.75 4.58579 12.4142 4.25 12 4.25C11.5858 4.25 11.25 4.58579 11.25 5L11.25 17.0745L7.55239 13.0483C7.27222 12.7432 6.79777 12.723 6.49269 13.0032C6.18761 13.2833 6.16742 13.7578 6.4476 14.0629L11.4476 19.5073C11.5896 19.662 11.79 19.75 12 19.75C12.21 19.75 12.4104 19.662 12.5524 19.5073L17.5524 14.0629C17.8326 13.7578 17.8124 13.2833 17.5073 13.0032Z"
    fill="#77757D"
  />
</svg> `;
const arrowUp = html`<svg
  width="24"
  height="24"
  viewBox="0 0 24 24"
  fill="currentColor"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M17.5073 10.9968C17.2022 11.277 16.7278 11.2568 16.4476 10.9517L12.75 6.92547L12.75 19C12.75 19.4142 12.4142 19.75 12 19.75C11.5858 19.75 11.25 19.4142 11.25 19L11.25 6.92547L7.55239 10.9517C7.27222 11.2568 6.79777 11.277 6.49269 10.9968C6.18761 10.7167 6.16742 10.2422 6.4476 9.93714L11.4476 4.4927C11.5896 4.33803 11.79 4.25 12 4.25C12.21 4.25 12.4104 4.33803 12.5524 4.4927L17.5524 9.93714C17.8326 10.2422 17.8124 10.7167 17.5073 10.9968Z"
    fill="#77757D"
  />
</svg> `;
const styles = css`
  ${unsafeCSS(dataViewCommonStyle('affine-data-view-record-detail'))}
  affine-data-view-record-detail {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 16px 20px;
    gap: 0;
    background-color: var(--affine-background-primary-color);
    border-radius: 8px;
    min-height: 100%;
    width: 100%;
    max-width: 540px;
    margin: 0 auto;
    box-sizing: border-box;
    overflow-y: auto;
  }

  .add-property {
    display: none !important;
  }

  .switch-row {
    display: none !important;
  }

  /* Compact metadata header bar */
  .detail-metadata-header {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 4px 0 8px;
    margin-bottom: 4px;
    position: relative;
    z-index: 50;
  }

  .metadata-item {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 6px;
    background: var(--affine-hover-color, rgba(0,0,0,0.04));
    cursor: pointer;
    transition: background 0.15s ease;
    min-height: 28px;
  }

  .metadata-item:hover {
    background: var(--affine-hover-color-filled, rgba(0,0,0,0.08));
  }

  .metadata-item .metadata-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--affine-text-secondary-color);
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-right: 2px;
  }

  .metadata-item .metadata-value {
    font-size: 12px;
    color: var(--affine-text-primary-color);
  }

  .metadata-item .metadata-value:empty::before {
    content: '-';
    color: var(--affine-text-disable-color);
  }

  .metadata-item-editing {
    background: var(--affine-hover-color-filled, rgba(0,0,0,0.08));
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 140px;
    position: relative;
    z-index: 100;
  }

  .metadata-editor {
    width: 100%;
    position: relative;
    z-index: 100;
  }

  .metadata-editor affine-multi-tag-select,
  .metadata-editor affine-database-date-cell-editing {
    width: 100% !important;
    min-width: 120px !important;
  }

  .metadata-editor .affine-select-cell-select,
  .metadata-editor .affine-database-date {
    z-index: 9999 !important;
    position: relative;
  }

  .metadata-editor .select-option-container,
  .affine-select-cell-select .select-option-container {
    z-index: 9999 !important;
    position: relative;
  }

  /* Title from slot header - prominent and clear */
  database-datasource-block-renderer {
    display: block;
    margin-bottom: 12px;
    border-bottom: none !important;
  }

  database-datasource-block-renderer .database-block-detail-header-icon {
    display: none !important;
  }

  database-datasource-block-renderer .inline-editor {
    font-size: 28px !important;
    font-weight: 600 !important;
    line-height: 1.3 !important;
    color: var(--affine-text-primary-color) !important;
  }

  database-datasource-block-renderer rich-text {
    font-size: 28px !important;
    font-weight: 600 !important;
    color: var(--affine-text-primary-color) !important;
  }

  database-datasource-block-renderer rich-text *,
  database-datasource-block-renderer .inline-editor *,
  database-datasource-block-renderer v-text,
  database-datasource-block-renderer v-line,
  database-datasource-block-renderer affine-text,
  database-datasource-block-renderer affine-text span,
  database-datasource-block-renderer v-text span,
  database-datasource-block-renderer span[style*="color"] {
    color: var(--affine-text-primary-color) !important;
    font-size: 28px !important;
    font-weight: 600 !important;
  }

  /* Title section fallback - prominent and clear */
  .detail-title-section {
    display: block !important;
    margin-bottom: 12px;
  }

  .detail-title-section affine-data-view-record-field {
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
  }

  .detail-title-section .field-left {
    display: none !important;
  }

  .detail-title-section .field-content {
    font-size: 28px !important;
    font-weight: 600 !important;
    padding: 0 !important;
    line-height: 1.3 !important;
    color: var(--affine-text-primary-color) !important;
  }

  .detail-title-section .field-content .affine-database-rich-text {
    font-size: 28px !important;
    font-weight: 600 !important;
    line-height: 1.3 !important;
  }

  /* Notes section - subtle, no background */
  .detail-notes-section {
    display: block !important;
    margin: 0 0 20px;
    padding: 0;
    background: transparent;
    border: none;
    min-height: auto;
  }

  .detail-notes-section affine-data-view-record-field {
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
  }

  .detail-notes-section .field-left {
    display: none !important;
  }

  .detail-notes-section .field-content {
    padding: 0 !important;
    font-size: 14px !important;
    color: var(--affine-text-secondary-color);
    min-height: 24px;
    background: transparent !important;
  }

  .detail-notes-section .field-content.empty::before {
    content: 'Notes...' !important;
    color: var(--affine-placeholder-color, rgba(0,0,0,0.3));
    font-style: normal;
    font-weight: 400;
  }

  .detail-notes-section .field-content:hover {
    background: transparent !important;
  }

  /* Hide regular fields (moved to header/notes) */
  affine-data-view-record-field {
    display: none !important;
  }

  .detail-title-section affine-data-view-record-field,
  .detail-notes-section affine-data-view-record-field {
    display: flex !important;
  }

  /* Fix z-index for portals and overlays */
  [data-floating-ui-portal] {
    z-index: 99999 !important;
  }

  affine-overlay-container,
  .affine-overlay-container {
    z-index: 99999 !important;
  }

  /* Custom scrollbar */
  affine-data-view-record-detail::-webkit-scrollbar,
  .pique-detail-original::-webkit-scrollbar {
    width: 5px;
  }

  affine-data-view-record-detail::-webkit-scrollbar-track,
  .pique-detail-original::-webkit-scrollbar-track {
    background: transparent;
  }

  affine-data-view-record-detail::-webkit-scrollbar-thumb,
  .pique-detail-original::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
  }

  /* Pique custom components */
  .pique-detail-wrapper {
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    box-sizing: border-box;
    min-height: 100%;
    position: relative;
  }

  .pique-detail-original {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 80px;
    min-height: 0;
  }

  .pique-action-container {
    flex: 0 0 auto;
    margin-top: 0;
    padding: 0;
    background-color: transparent;
    position: relative;
  }

  .pique-action-plan-container {
    flex: 0 0 auto;
    padding: 0 16px;
    margin: 24px 0 16px;
    background-color: transparent;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    position: relative;
    z-index: 1;
  }

  pique-action-panel {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 0;
    margin-top: 0;
    margin-bottom: 8px;
    position: relative;
  }

  pique-action-plan-panel {
    display: block;
    width: 100%;
    box-sizing: border-box;
  }
`;

const HEADER_COLUMN_NAMES = ['Status', 'Priority', 'Due Date', 'Deadline', "Who's Responsible?", 'Assignee', 'Assigned To'];
const NOTES_COLUMN_NAMES = ['My Notes', 'Close Loop'];
const HIDDEN_COLUMN_NAMES = ['Remind Later', 'remindLater', 'Context'];

@customElement('affine-data-view-record-detail')
export class RecordDetail extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  _clickAddProperty = () => {
    popFilterableSimpleMenu(
      this.addPropertyButton,
      this.view.allColumnConfig.map(config => {
        return {
          type: 'action',
          name: config.name,
          icon: renderUniLit(this.view.getIcon(config.type)),
          select: () => {
            this.view.columnAdd('end', config.type);
          },
        };
      })
    );
  };

  static override styles = styles;

  titleColumn$ = computed(() => {
    const cols = this.view.detailColumns$.value;
    for (const id of cols) {
      const col = this.view.columnGet(id);
      if (col.type === 'title') return col;
    }
    return null;
  });

  headerColumns$ = computed(() => {
    return this.view.detailColumns$.value
      .map(id => this.view.columnGet(id))
      .filter(col => HEADER_COLUMN_NAMES.includes(col.name));
  });

  notesColumn$ = computed(() => {
    const cols = this.view.detailColumns$.value;
    for (const id of cols) {
      const col = this.view.columnGet(id);
      if (NOTES_COLUMN_NAMES.includes(col.name)) return col;
    }
    return null;
  });

  columns$ = computed(() => {
    return this.view.detailColumns$.value
      .filter(id => {
        const col = this.view.columnGet(id);
        if (col.type === 'title') return false;
        if (HEADER_COLUMN_NAMES.includes(col.name)) return false;
        if (NOTES_COLUMN_NAMES.includes(col.name)) return false;
        if (HIDDEN_COLUMN_NAMES.includes(col.name)) return false;
        return true;
      })
      .map(id => this.view.columnGet(id));
  });

  @state()
  accessor editingHeaderColumn: string | null = null;

  detailSlots?: DetailSlots;

  selection = new DetailSelection(this);

  private get readonly() {
    return this.view.readonly$.value;
  }

  private getCellDisplayValue(column: Column): string {
    try {
      const cell = column.cellGet(this.rowId);
      const val = cell?.stringValue$?.value;

      // For date columns, format as human-readable
      if (column.type === 'date' && val) {
        const timestamp = Number(val);
        if (!isNaN(timestamp) && timestamp > 0) {
          const date = new Date(timestamp);
          const now = new Date();
          const isThisYear = date.getFullYear() === now.getFullYear();
          const options: Intl.DateTimeFormatOptions = {
            month: 'short',
            day: 'numeric',
            ...(isThisYear ? {} : { year: 'numeric' }),
          };
          return date.toLocaleDateString('en-US', options);
        }
      }

      return typeof val === 'string' ? val : '';
    } catch {
      return '';
    }
  }

  private getShortLabel(name: string): string {
    if (name === "Who's Responsible?" || name === 'Assignee' || name === 'Assigned To') return 'Assignee';
    if (name === 'Due Date' || name === 'Deadline') return 'Due';
    return name;
  }

  private handleMetadataClick(column: Column, e: MouseEvent) {
    e.stopPropagation();
    if (this.readonly) return;
    this.editingHeaderColumn = column.id;
    this.selection.selection = {
      propertyId: column.id,
      isEditing: true,
    };
  }

  private renderMetadataItem(column: Column) {
    const value = this.getCellDisplayValue(column);
    const label = this.getShortLabel(column.name);
    const isEditing = this.editingHeaderColumn === column.id;

    if (isEditing) {
      const cell = column.cellGet(this.rowId);
      const renderer = column.renderer$.value;
      if (renderer?.edit) {
        const props: CellRenderProps = {
          cell,
          isEditing: true,
          selectCurrentCell: (editing: boolean) => {
            if (!editing) {
              this.editingHeaderColumn = null;
              this.selection.selection = undefined;
            }
          },
        };
        return html`
          <div class="metadata-item metadata-item-editing" @click=${(e: MouseEvent) => e.stopPropagation()}>
            <span class="metadata-label">${label}</span>
            <div class="metadata-editor">
              ${renderUniLit(renderer.edit, props)}
            </div>
          </div>
        `;
      }
    }

    return html`
      <div class="metadata-item" @click=${(e: MouseEvent) => this.handleMetadataClick(column, e)}>
        <span class="metadata-label">${label}</span>
        <span class="metadata-value">${value}</span>
      </div>
    `;
  }

  private renderCompactHeader() {
    const headerCols = this.headerColumns$.value;
    if (headerCols.length === 0) return nothing;

    return html`
      <div class="detail-metadata-header">
        ${headerCols.map(col => this.renderMetadataItem(col))}
      </div>
    `;
  }

  private renderTitleSection() {
    const titleCol = this.titleColumn$.value;
    if (!titleCol) return nothing;

    return html`
      <div class="detail-title-section">
        ${keyed(
          this.rowId,
          html`<affine-data-view-record-field
            .view="${this.view}"
            .column="${titleCol}"
            .rowId="${this.rowId}"
            data-column-id="${titleCol.id}"
          ></affine-data-view-record-field>`
        )}
      </div>
    `;
  }

  private renderNotesSection() {
    const notesCol = this.notesColumn$.value;
    if (!notesCol) return nothing;

    return html`
      <div class="detail-notes-section">
        ${keyed(
          this.rowId,
          html`<affine-data-view-record-field
            .view="${this.view}"
            .column="${notesCol}"
            .rowId="${this.rowId}"
            data-column-id="${notesCol.id}"
          ></affine-data-view-record-field>`
        )}
      </div>
    `;
  }

  private renderSlotHeader() {
    const header = this.detailSlots?.header;
    if (header) {
      const props: DetailSlotProps = {
        view: this.view,
        rowId: this.rowId,
      };
      return renderUniLit(header, props);
    }
    return undefined;
  }

  private renderNote() {
    const note = this.detailSlots?.note;
    if (note) {
      const props: DetailSlotProps = {
        view: this.view,
        rowId: this.rowId,
      };
      return renderUniLit(note, props);
    }
    return undefined;
  }

  override connectedCallback() {
    super.connectedCallback();

    this.disposables.addFromEvent(this, 'click', e => {
      e.stopPropagation();
      this.selection.selection = undefined;
      this.editingHeaderColumn = null;
    });
    this.dataset.widgetId = 'affine-detail-widget';
    this.detailSlots = this.view.detailSlots;
  }

  hasNext() {
    return this.view.rowGetNext(this.rowId) != null;
  }

  hasPrev() {
    return this.view.rowGetPrev(this.rowId) != null;
  }

  nextRow() {
    const rowId = this.view.rowGetNext(this.rowId);
    if (rowId == null) return;
    this.rowId = rowId;
    this.requestUpdate();
  }

  prevRow() {
    const rowId = this.view.rowGetPrev(this.rowId);
    if (rowId == null) return;
    this.rowId = rowId;
    this.requestUpdate();
  }

  override render() {
    const otherColumns = this.columns$.value;

    return html`
      <div
        style="max-width: 100%;width: 100%;display: flex;flex-direction: column;margin: 0 auto;flex: 1;min-height: 0;overflow-y: visible"
        data-current-row-id="${this.rowId}"
      >
        ${keyed(this.rowId, this.renderCompactHeader())}
        ${keyed(this.rowId, this.renderSlotHeader())}
        ${keyed(this.rowId, this.renderTitleSection())}
        ${keyed(this.rowId, this.renderNotesSection())}
        ${repeat(
          otherColumns,
          v => v.id,
          column => keyed(
            this.rowId,
            html`<affine-data-view-record-field
              .view="${this.view}"
              .column="${column}"
              .rowId="${this.rowId}"
              data-column-id="${column.id}"
            ></affine-data-view-record-field>`
          )
        )}
      </div>
      ${keyed(this.rowId, this.renderNote())}
    `;
  }

  @query('.add-property')
  accessor addPropertyButton!: HTMLElement;

  @property({ attribute: false })
  accessor rowId!: string;

  @property({ attribute: false })
  accessor view!: SingleView;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-data-view-record-detail': RecordDetail;
  }
}
